import { useMemo } from "react";

import { defaultPrivateSpaceName, defaultTeamSpaceName } from "../config";
import type { ProjectID, ProjectRole, TeamID } from "../types";
import useAccount from "./useAccount";
import type { AccountData } from "./useAccountTypes";
import { type ProjectData, useProject } from "./useProject";
import { useWebExport, type WebExportData } from "./useWebExport";

export type UnifiedProjectAndWebExportData = {
  teamId: TeamID;
  projectId: ProjectID;
  projectName: string;
  spaceName: string | undefined;
  lastModifiedAt: Date;
  userName: string | undefined;
  userRole: ProjectRole;
  thumbnailUrl: string | undefined;
  isCurrentUser: boolean;
};
function unifyProjectAndWebExport(
  projectData: ProjectData | undefined,
  webExportData: WebExportData | undefined,
  accountData: AccountData | undefined
): UnifiedProjectAndWebExportData | undefined {
  if (projectData) {
    const user = projectData?.users?.find(
      (user) => user.userId === projectData.latestRevision.uploadedByUserId
    );
    const userName =
      user && user.userId === accountData?.id ? (user.name ? `${user.name}` : "You") : user?.name;

    const spaceName =
      projectData.space?.spaceName ?? spaceTypeNameMap[projectData.space?.spaceType ?? ""];

    return {
      teamId: projectData.teamId,
      projectId: projectData.project.projectId,
      projectName: projectData.project.name,
      spaceName,
      lastModifiedAt: projectData.latestRevision.lastModifiedAt,
      userName,
      userRole: projectData.userRole,
      thumbnailUrl: projectData.latestRevision.thumbnailDownloads?.light?.url,
      isCurrentUser: Boolean(user && user.userId === accountData?.id),
    };
  }

  if (webExportData) {
    const userName =
      webExportData.webExport.uploadedByUserId === accountData?.id
        ? accountData.name
          ? `${accountData.name}`
          : "You"
        : undefined;

    return {
      teamId: webExportData.teamId,
      projectId: webExportData.webExport.projectId,
      projectName: webExportData.webExport.title,
      spaceName: undefined,
      lastModifiedAt: webExportData.webExport.uploadedAt,
      userName,
      userRole: webExportData.userRole,
      thumbnailUrl: webExportData.revision?.thumbnailDownloads?.light?.url,
      isCurrentUser: Boolean(webExportData.webExport.uploadedByUserId === accountData?.id),
    };
  }

  return undefined;
}

const spaceTypeNameMap: Record<string, string> = {
  private: defaultPrivateSpaceName,
  team: defaultTeamSpaceName,
};

export const useProjectOrWebExport = () => {
  const { data: projectData, isLoading: isProjectLoading } = useProject();
  const { data: webExportData, isLoading: isWebExportLoading } = useWebExport();
  const { data: accountData, isLoading: isAccountLoading } = useAccount();

  return useMemo(() => {
    return {
      data: unifyProjectAndWebExport(projectData, webExportData, accountData),
      isLoading: isProjectLoading || isWebExportLoading || isAccountLoading,
    };
  }, [
    projectData,
    webExportData,
    accountData,
    isProjectLoading,
    isWebExportLoading,
    isAccountLoading,
  ]);
};
